// Per device on purpose, not per account. `/look.js` applies the same look before
// first paint; keep the two in step.
import { useCallback, useEffect, useState } from 'react';
import { oneOf } from './view.ts';

export const THEMES = ['auto', 'dark', 'light'] as const;
export const MONIES = ['green-red', 'blue-red'] as const;

interface Look {
  theme: (typeof THEMES)[number];
  money: (typeof MONIES)[number];
}

type Face = 'dark' | 'light';

const DEFAULT_LOOK: Look = { theme: 'auto', money: 'green-red' };

const LOOK_KEY = 'alphadesk.look';

const LIGHT_QUERY = '(prefers-color-scheme: light)';

// The dark one matches the manifest's `theme_color`.
const THEME_COLOUR: Record<Face, string> = {
  dark: '#151412',
  light: '#fdfcf9',
};

function parseLook(raw: string | null): Look {
  let held: unknown;
  try {
    held = raw === null ? null : JSON.parse(raw);
  } catch {
    held = null;
  }
  const record = typeof held === 'object' && held !== null ? (held as Record<string, unknown>) : {};
  return {
    theme: oneOf(THEMES, record.theme) ?? DEFAULT_LOOK.theme,
    money: oneOf(MONIES, record.money) ?? DEFAULT_LOOK.money,
  };
}

function deviceFace(): Face {
  return window.matchMedia(LIGHT_QUERY).matches ? 'light' : 'dark';
}

// Safari private windows throw on localStorage access.
function readLook(): Look {
  try {
    return parseLook(window.localStorage.getItem(LOOK_KEY));
  } catch {
    return DEFAULT_LOOK;
  }
}

function writeLook(look: Look): void {
  try {
    window.localStorage.setItem(LOOK_KEY, JSON.stringify(look));
  } catch {}
}

function applyLook(look: Look, device: Face): void {
  const theme = look.theme === 'auto' ? device : look.theme;
  const root = document.documentElement;
  root.setAttribute('data-theme', theme);
  root.setAttribute('data-money', look.money);
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOUR[theme]);
}

export interface Held {
  look: Look;
  /** What Auto comes to here now. */
  device: Face;
  set: (patch: Partial<Look>) => void;
}

export function useLook(): Held {
  const [look, setLook] = useState(readLook);
  const [device, setDevice] = useState(deviceFace);

  useEffect(() => {
    const query = window.matchMedia(LIGHT_QUERY);
    const restate = () => setDevice(deviceFace());
    query.addEventListener('change', restate);
    return () => query.removeEventListener('change', restate);
  }, []);

  useEffect(() => applyLook(look, device), [look, device]);

  useEffect(() => {
    const sync = (event: StorageEvent) => {
      if (event.key === LOOK_KEY) setLook(parseLook(event.newValue));
    };
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);

  const set = useCallback((patch: Partial<Look>) => {
    setLook((current) => {
      const next = { ...current, ...patch };
      writeLook(next);
      return next;
    });
  }, []);

  return { look, device, set };
}
